const STATIC_FAVICON = "/favicon.svg";
const LOOP_PATH = new Path2D(
  "M54 100h22c0 29 20 47 49 47s49-18 49-47h22c0 41-31 68-71 68s-71-27-71-68Z",
);

type LandingFrame = {
  x: number;
  y: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
};

function clamp(value: number, minimum = 0, maximum = 1) {
  return Math.min(maximum, Math.max(minimum, value));
}

function mix(from: number, to: number, progress: number) {
  return from + (to - from) * progress;
}

function easeOutCubic(progress: number) {
  return 1 - Math.pow(1 - progress, 3);
}

function easeOutBack(progress: number) {
  const overshoot = 1.35;
  const shifted = progress - 1;
  return 1 + (overshoot + 1) * Math.pow(shifted, 3) + overshoot * Math.pow(shifted, 2);
}

function frameFor(progress: number): LandingFrame {
  if (progress < 0.48) {
    const phase = easeOutCubic(progress / 0.48);
    return {
      x: mix(-18, 0, phase),
      y: mix(-56, 3, phase),
      rotation: mix(-0.34, 0.025, phase),
      scaleX: mix(0.58, 0.9, phase),
      scaleY: mix(0.72, 1.08, phase),
    };
  }

  if (progress < 0.68) {
    const phase = easeOutCubic((progress - 0.48) / 0.2);
    return {
      x: 0,
      y: mix(3, 7, phase),
      rotation: mix(0.025, 0, phase),
      scaleX: mix(0.9, 1.2, phase),
      scaleY: mix(1.08, 0.74, phase),
    };
  }

  const phase = easeOutBack((progress - 0.68) / 0.32);
  return {
    x: 0,
    y: mix(7, 0, phase),
    rotation: 0,
    scaleX: mix(1.2, 1, phase),
    scaleY: mix(0.74, 1, phase),
  };
}

function drawFriendlyPeriod(
  context: CanvasRenderingContext2D,
  frame: LandingFrame,
  dotProgress: number,
) {
  const scale = 64 / 250;
  context.clearRect(0, 0, 64, 64);
  context.fillStyle = "#191713";

  context.save();
  context.scale(scale, scale);
  context.translate(125 + frame.x, 134 + frame.y);
  context.rotate(frame.rotation);
  context.scale(frame.scaleX, frame.scaleY);
  context.translate(-125, -134);
  context.fill(LOOP_PATH);
  context.restore();

  const dotScale = mix(0.72, 1, easeOutBack(dotProgress));
  context.save();
  context.scale(scale, scale);
  context.translate(125, 100);
  context.scale(dotScale, dotScale);
  context.beginPath();
  context.arc(0, 0, 22, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

export function playFriendlyFavicon() {
  const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!link) return undefined;

  link.href = STATIC_FAVICON;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return undefined;
  }

  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const context = canvas.getContext("2d");
  if (!context) return undefined;

  const duration = 1320;
  const startedAt = performance.now();
  let animationFrame = 0;
  let lastPaint = 0;

  const paint = (now: number) => {
    const elapsed = now - startedAt;
    const progress = clamp(elapsed / duration);

    if (elapsed - lastPaint >= 28 || progress === 1) {
      drawFriendlyPeriod(context, frameFor(progress), clamp(progress / 0.3));
      link.href = canvas.toDataURL("image/png");
      lastPaint = elapsed;
    }

    if (progress < 1) animationFrame = requestAnimationFrame(paint);
  };

  animationFrame = requestAnimationFrame(paint);

  return () => {
    cancelAnimationFrame(animationFrame);
    link.href = STATIC_FAVICON;
  };
}
